(function () {
  const state = {};
  const subscribers = {}; // { key: [ entries ] }
  const RESERVED = new Set(['true', 'false', 'null', 'undefined']);

  function applyBinding(el, type, value, className) {
    if (type === 'text') el.textContent = value ?? '';
    else if (type === 'html') el.innerHTML = value ?? '';
    else if (type === 'value') el.value = value ?? '';
    else if (type === 'checked') el.checked = Boolean(value);
    else if (type === 'show') el.style.display = value ? '' : 'none';
    else if (type === 'css' && className) el.classList.toggle(className, Boolean(value));
  }

  function notify(key, newVal) {
    (subscribers[key] || []).forEach(entry => {
      let v;
      if (entry.evaluate) {
        // Expression entry: recompute from full state
        v = entry.evaluate(state);
      } else {
        // Simple key / !key
        v = entry.isNegated ? !newVal : newVal;
      }
      applyBinding(entry.el, entry.type, v, entry.className);
    });
  }

  function addSubscriber(el, type, keyRaw, className) {
    // Hover is a simple convenience
    if (type === 'hover') {
      const hoverClass = keyRaw.trim();
      el.addEventListener('mouseenter', () => el.classList.add(hoverClass));
      el.addEventListener('mouseleave', () => el.classList.remove(hoverClass));
      return;
    }

    // Expression?
    const isExpression = /&&|\|\||\(|\)|\!/.test(keyRaw) && !/^\s*!?\w+\s*$/.test(keyRaw);
    const trimmedClass = (className || '').trim();

    if (isExpression) {
      const deps = Array.from(new Set((keyRaw.match(/\b[a-zA-Z_]\w*\b/g) || [])))
        .filter(k => !RESERVED.has(k));

      // Compile a safe reader that only pulls identifiers from state
      const compiled = keyRaw.replace(/\b([a-zA-Z_]\w*)\b/g, (m, id) => {
        if (RESERVED.has(id)) return id;
        return `(state["${id}"])`;
      });

      const fn = new Function('state', `return !!(${compiled});`);
      const entry = { el, type, className: trimmedClass, evaluate: s => fn(s) };

      // Subscribe this entry to all deps so notify() re-evals it when any dep changes
      deps.forEach(d => {
        if (!subscribers[d]) subscribers[d] = [];
        subscribers[d].push(entry);
      });

      // Initial apply (state may still be defaults)
      applyBinding(el, type, entry.evaluate(state), trimmedClass);
      return;
    }

    // Simple key / !key
    const isNegated = keyRaw.trim().startsWith('!');
    const key = isNegated ? keyRaw.trim().slice(1) : keyRaw.trim();
    if (!subscribers[key]) subscribers[key] = [];
    subscribers[key].push({ el, type, isNegated, className: trimmedClass });

    // Input <-> state wiring (only for simple keys)
    if (type === 'value' && el.tagName === 'INPUT') {
      el.addEventListener('input', () => { state[key] = el.value; });
    }
    if (type === 'checked' && el.type === 'checkbox') {
      el.addEventListener('change', () => { state[key] = el.checked; });
    }
  }

  window.createTwoWayBindings = function (keysWithDefaults) {
    // Parse bindings before defining properties is fine; we do an initial apply afterward.
    const bindings = document.querySelectorAll('[data-bind]');
    bindings.forEach(el => {
      el.dataset.bind.split(',').map(b => b.trim()).forEach(binding => {
        const [type, rawKey, cls] = binding.split(':');
        addSubscriber(el, type.trim(), (rawKey || '').trim(), (cls || '').trim());
      });
    });

    // Define observable props
    Object.entries(keysWithDefaults).forEach(([key, initial]) => {
      let internalValue = initial;
      Object.defineProperty(state, key, {
        get() { return internalValue; },
        set(newVal) {
          if (internalValue === newVal) return;
          internalValue = newVal;
          notify(key, newVal);
        }
      });
      // Initial notify to sync simple subscribers
      notify(key, initial);
    });

    return state;
  };
})();
